import { TextEditor } from 'handsontable/editors';
import Handsontable from 'handsontable';
import React from 'react';
import { createRoot, Root } from 'react-dom/client';

import { enumToLabel } from '../../services/excel-table.service';
import { EPlanFormulationProductType } from '../../types/plan.enum';

import type { ComponentType } from 'react';
import type { SearchOption } from '@/types/search';

/**
 * Convert full-width characters to half-width (digits, period, colon).
 * Normalizes time-like input: full-width colon and spaces around ":" → "11:30".
 */
export function convertToHalf(str: string): string {
  return str
    .replace(/[０-９]/g, (s) => String.fromCharCode(s.charCodeAt(0) - 0xfee0))
    .replace(/．/g, '.')
    .replace(/：/g, ':')
    .replace(/\s*:\s*/g, ':');
}

/**
 * Check if editor should be disabled for column 11 (内容) based on product type
 * Shared logic for all editor classes
 */
function shouldSkipEditorForColumn11(hot: Handsontable.Core, cellArr: number[]): boolean {
  if (cellArr.length && cellArr[3] === 11) {
    const productTypeValue = hot.getDataAtCell(cellArr[2], 10);
    const otherProductTypeLabel = enumToLabel.formulationProductType(
      EPlanFormulationProductType.OTHER
    );
    return productTypeValue === otherProductTypeLabel;
  }
  return false;
}

/**
 * Initialize editor value from original value
 * Shared logic for all editor classes
 * @returns true if initialization succeeded, false if should return early
 */
function initializeEditorValue(
  editor: { value: { value: unknown }; TEXTAREA: HTMLElement },
  originalValue: unknown
): boolean {
  editor.value = { value: undefined };
  if (originalValue) {
    editor.value.value = originalValue;
    if (!(editor.TEXTAREA instanceof HTMLTextAreaElement)) {
      return false;
    }
    editor.TEXTAREA.value = originalValue as string;
  }
  return true;
}

/**
 * Create or get React container element
 * Shared logic for all editor classes
 */
function getOrCreateReactContainer(editor: {
  reactContainer: HTMLElement | null;
  TEXTAREA_PARENT: HTMLElement;
}): HTMLElement {
  if (!editor.reactContainer) {
    editor.reactContainer = document.createElement('div');
    editor.reactContainer.style.width = '100%';
    editor.reactContainer.style.height = '100%';
    editor.TEXTAREA_PARENT.appendChild(editor.reactContainer);
  }
  return editor.reactContainer;
}

/**
 * Get cell element for positioning
 * Shared logic for all editor classes
 */
function getCellElement(hot: Handsontable.Core, cellArr: number[]): HTMLElement | undefined {
  if (cellArr.length >= 2) {
    const row = cellArr[0];
    const col = cellArr[1];
    return hot.getCell(row, col) as HTMLElement;
  }
  return undefined;
}

/**
 * Clean up input handler
 * Shared logic for autocomplete and dropdown editors
 */
function cleanupInputHandler(editor: {
  _inputHandler?: (event: Event) => void;
  TEXTAREA: HTMLElement;
}): void {
  if (editor._inputHandler && editor.TEXTAREA instanceof HTMLTextAreaElement) {
    editor.TEXTAREA.removeEventListener('input', editor._inputHandler);
    editor._inputHandler = undefined;
  }
}

/**
 * Clean up React root and container
 * Shared logic for all editor classes
 */
function cleanupReactResources(editor: {
  root: Root | null;
  reactContainer: HTMLElement | null;
}): void {
  if (editor.root) {
    editor.root.unmount();
    editor.root = null;
  }
  if (editor.reactContainer) {
    editor.reactContainer.remove();
    editor.reactContainer = null;
  }
}

/**
 * Clean up input handler and React root before reopening editor
 * Shared logic for autocomplete and dropdown editors
 * Note: Does not remove container to allow reuse for better performance
 */
function cleanupBeforeReopen(editor: {
  _inputHandler?: (event: Event) => void;
  TEXTAREA: HTMLElement;
  root: Root | null;
}): void {
  cleanupInputHandler(editor);
  if (editor.root) {
    editor.root.unmount();
    editor.root = null;
  }
}

/**
 * Initialize common editor state and get cell element
 * Shared logic for autocomplete and dropdown editors
 * @returns object with cellElement and shouldReturn flag, or null if should return early
 */
type BaseReactEditorState = {
  TEXTAREA: HTMLElement;
  TEXTAREA_PARENT: HTMLElement;
  originalValue: unknown;
  reactContainer: HTMLElement | null;
  hot: Handsontable.Core;
  _inputHandler?: (event: Event) => void;
  value: { value: unknown };
};

function initializeEditorState(
  editor: BaseReactEditorState,
  cellArr: number[]
): { cellElement: HTMLElement | undefined; shouldReturn: boolean } | null {
  editor.TEXTAREA.setAttribute('aria-hidden', 'false');
  cleanupInputHandler(editor);

  if (!initializeEditorValue(editor, editor.originalValue)) {
    if (!(editor.TEXTAREA instanceof HTMLTextAreaElement)) {
      return null; // Should return early
    }
  }

  if (shouldSkipEditorForColumn11(editor.hot, cellArr)) {
    return null; // Should return early
  }

  const reactContainer = getOrCreateReactContainer(editor);
  editor.reactContainer = reactContainer;

  return {
    cellElement: getCellElement(editor.hot, cellArr),
    shouldReturn: false,
  };
}

/**
 * Setup input handler for editor with re-render callback
 * Shared logic for autocomplete and dropdown editors
 */
function setupInputHandler(
  editor: {
    TEXTAREA: HTMLElement;
    _inputHandler?: (event: Event) => void;
    value: { value: unknown };
  },
  renderComponent: () => void
): void {
  const textarea = editor.TEXTAREA;
  if (textarea instanceof HTMLTextAreaElement) {
    if (editor._inputHandler) {
      textarea.removeEventListener('input', editor._inputHandler);
    }
    editor._inputHandler = (event: Event) => {
      if (!(event.target instanceof HTMLTextAreaElement)) return;
      editor.value.value = convertToHalf(event.target.value);
      renderComponent();
    };
    textarea.addEventListener('input', editor._inputHandler);
  }
}

/**
 * Create common React editor class with shared logic
 * Used by both createAutocompleteEditorClass and createDropdownEditorClass
 */
function createCommonReactEditorClass<TProps extends ComponentEditorProps>(
  component: ComponentType<TProps>,
  getComponentProps: (
    editor: {
      root: Root | null;
      value: { value: unknown };
      TEXTAREA: HTMLElement;
      finishEditing: () => void;
    },
    cellElement: HTMLElement | undefined,
    cellArr: number[]
  ) => Omit<TProps, 'modelValue' | 'onUpdateModelValue' | 'onFinished' | 'cellElement'>
) {
  class ReactEditor extends TextEditor {
    root: Root | null = null;
    reactContainer: HTMLElement | null = null;
    value: { value: unknown } = { value: undefined };
    _inputHandler?: (event: Event) => void;

    constructor(hotInstance: Handsontable.Core) {
      super(hotInstance);
      this.showEditor();
    }

    /**
     * When the column defines `maxLength` (e.g. placing method), copy it to the editor textarea.
     * The browser enforces `textarea.maxLength`, so the user cannot enter more characters than allowed.
     * Same pattern as `MaxLengthEditor` in handsontable.config.ts.
     */
    prepare(
      row: number,
      col: number,
      prop: string | number,
      td: HTMLTableCellElement,
      originalValue: unknown,
      cellProperties: Handsontable.CellProperties
    ): void {
      super.prepare(row, col, prop, td, originalValue, cellProperties);
      const maxLength = (cellProperties as { maxLength?: number }).maxLength;
      if (this.TEXTAREA instanceof HTMLTextAreaElement) {
        if (maxLength != null && maxLength > 0) {
          this.TEXTAREA.maxLength = maxLength;
        } else {
          this.TEXTAREA.removeAttribute('maxLength');
        }
      }
    }

    showEditor() {
      const cellArr = this.hot.getSelectedLast() || [];
      const initResult = initializeEditorState(this, cellArr);
      if (!initResult) {
        return;
      }
      const cellElement = initResult.cellElement;

      // Create React root only if it doesn't exist (reuse for better performance)
      if (!this.root && this.reactContainer) {
        this.root = createRoot(this.reactContainer);
      }

      // Render function to update component when value changes
      const renderComponent = () => {
        if (!this.root) return;
        const additionalProps = getComponentProps(
          {
            root: this.root,
            value: this.value,
            TEXTAREA: this.TEXTAREA,
            finishEditing: () => this.finishEditing(),
          },
          cellElement,
          cellArr
        );
        this.root.render(
          React.createElement(component, {
            modelValue: this.value,
            onUpdateModelValue: (newValue: string) => {
              this.value.value = convertToHalf(newValue);
              if (!(this.TEXTAREA instanceof HTMLTextAreaElement)) return;
              this.TEXTAREA.value = convertToHalf(newValue);
              // Re-render component to update filtered options
              renderComponent();
            },
            onFinished: () => this.finishEditing(),
            handsontable: this.hot,
            // Enter is handled in React (capture) so the grid shortcut never runs; mirror built-in close + move.
            onEnterCommit: (event: KeyboardEvent) => {
              this.finishEditing(false, false, () => {
                (
                  this.hot as Handsontable.Core & {
                    _getEditorManager?: () => {
                      moveSelectionAfterEnter: (e: KeyboardEvent) => void;
                    };
                  }
                )
                  ._getEditorManager?.()
                  .moveSelectionAfterEnter(event);
              });
            },
            cellElement: cellElement,
            ...additionalProps,
          } as unknown as TProps)
        );
      };

      // Initial render
      renderComponent();

      // Setup input handler to trigger re-render
      setupInputHandler(this, renderComponent);
    }

    destroy() {
      cleanupInputHandler(this);
      cleanupReactResources(this);
    }

    open(event?: Event) {
      cleanupBeforeReopen(this);
      this.showEditor();
      super.open(event);
    }

    close() {
      this.destroy();
      super.close();
    }

    setValue(value: unknown) {
      this.value.value = value;
    }

    getValue() {
      return this.value.value;
    }
  }

  return ReactEditor;
}

export interface ComponentEditorProps {
  modelValue: { value: unknown };
  onUpdateModelValue: (newValue: string) => void;
  onFinished: () => void;
  cellElement?: HTMLElement; // Optional cell element for positioning
}

export const createReactComponentEditorClass = (
  component: ComponentType<ComponentEditorProps>,
  maxlength?: number
) => {
  class ReactEditor extends TextEditor {
    root: Root | null = null;
    reactContainer: HTMLElement | null = null;
    value: { value: unknown } = { value: undefined };

    constructor(hotInstance: Handsontable.Core) {
      super(hotInstance);
      this.showEditor();
    }

    showEditor() {
      const cellArr = this.hot.getSelectedLast() || [];
      if (maxlength) {
        this.TEXTAREA.setAttribute('maxlength', String(maxlength));
      }
      this.TEXTAREA.setAttribute('aria-hidden', 'false');
      this.TEXTAREA.addEventListener('input', (event: Event) => {
        if (!(event.target instanceof HTMLTextAreaElement)) return;
        this.value.value = convertToHalf(event.target.value);
      });

      this.value = { value: undefined };
      if (this.originalValue) {
        this.value.value = this.originalValue;
        if (!(this.TEXTAREA instanceof HTMLTextAreaElement)) return;
        this.TEXTAREA.value = this.originalValue as string;
      }

      if (cellArr.length && cellArr[3] === 11) {
        const productTypeValue = this.hot.getDataAtCell(cellArr[2], 10);
        const otherProductTypeLabel = enumToLabel.formulationProductType(
          EPlanFormulationProductType.OTHER
        );
        if (productTypeValue === otherProductTypeLabel) {
          return;
        }
      }

      // Create a container div for React component to preserve TEXTAREA_PARENT structure
      if (!this.reactContainer) {
        this.reactContainer = document.createElement('div');
        this.reactContainer.style.width = '100%';
        this.reactContainer.style.height = '100%';
        // Append container to TEXTAREA_PARENT while keeping TEXTAREA
        this.TEXTAREA_PARENT.appendChild(this.reactContainer);
      }

      // Get cell element for positioning (if needed by component)
      let cellElement: HTMLElement | undefined;
      if (cellArr.length >= 2) {
        const row = cellArr[0];
        const col = cellArr[1];
        cellElement = this.hot.getCell(row, col) as HTMLElement;
      }

      // Create React root and render component in the container
      this.root = createRoot(this.reactContainer);
      this.root.render(
        React.createElement(component, {
          modelValue: this.value,
          onUpdateModelValue: (newValue: string) => {
            this.value.value = convertToHalf(newValue);
            if (!(this.TEXTAREA instanceof HTMLTextAreaElement)) return;
            this.TEXTAREA.value = convertToHalf(newValue);
          },
          onFinished: () => this.finishEditing(),
          cellElement: cellElement,
        })
      );
    }

    destroy() {
      if (this.root) {
        this.root.unmount();
        this.root = null;
      }
      // Remove the container but keep TEXTAREA_PARENT intact
      this.reactContainer?.remove();
      this.reactContainer = null;
    }

    open(event?: Event) {
      this.destroy();
      this.showEditor();
      super.open(event);
    }

    close() {
      this.destroy();
      super.close();
    }

    setValue(value: unknown) {
      this.value.value = value;
    }

    getValue() {
      return this.value.value;
    }
  }

  return ReactEditor;
};

/**
 * Create React component editor class with autocomplete options support
 * Similar to createReactComponentEditorClass but allows passing options to the component
 * @param component - React component that accepts ComponentEditorProps with options
 * @param getOptions - Function to get options based on column index
 * @returns Handsontable editor class
 */
export const createAutocompleteEditorClass = (
  component: ComponentType<ComponentEditorProps & { options?: SearchOption[] }>,
  getOptions: (col: number) => SearchOption[]
) => {
  return createCommonReactEditorClass(component, (_editor, _cellElement, cellArr) => {
    let options: SearchOption[] = [];
    if (cellArr.length >= 2) {
      const col = cellArr[1];
      options = getOptions(col);
    }
    return { options };
  });
};

/**
 * Create React component editor class with dropdown options support
 * Similar to createAutocompleteEditorClass but for dropdown (string array instead of SearchOption[])
 * @param component - React component that accepts ComponentEditorProps with options and canEnterAnotherValue
 * @param getOptions - Function to get options based on column index
 * @param getCanEnterAnotherValue - Function to get canEnterAnotherValue based on column index
 * @returns Handsontable editor class
 */
export const createDropdownEditorClass = (
  component: ComponentType<
    ComponentEditorProps & { options?: string[]; canEnterAnotherValue?: boolean }
  >,
  getOptions: (col: number) => string[],
  getCanEnterAnotherValue?: (col: number) => boolean
) => {
  return createCommonReactEditorClass(component, (_editor, _cellElement, cellArr) => {
    let options: string[] = [];
    let canEnterAnotherValue = false;
    if (cellArr.length >= 2) {
      const col = cellArr[1];
      options = getOptions(col);
      if (getCanEnterAnotherValue) {
        canEnterAnotherValue = getCanEnterAnotherValue(col);
      }
    }
    return { options, canEnterAnotherValue };
  });
};
